import { useCallback, useEffect, useState } from 'react';
import { IFlowConfig } from '@spotify-internal/content-platform-ui';

// enrichard-response-receiver's response is a plain JSON object matching the
// wire/interface shape of content-platform-ui's generated FlowConfig proto
// (all fields optional), rather than the "hydrated" FlowConfig class type
// that <Form>'s config prop is typed against. Consumers cast to FlowConfig
// at that boundary; see ContributorCreditsForm.
export type FlowConfig = IFlowConfig;

interface State {
  loading: boolean;
  configuration: FlowConfig | null;
  error?: string;
  // HTTP status of the failed request, when available — lets callers tell a
  // client-side input problem (4xx, safe to show enrichard's own message)
  // apart from a service failure (5xx, show a generic fallback instead).
  errorStatus?: number;
}

const initialState: State = { loading: true, configuration: null };

class FormConfigRequestError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

export const useEnrichardFormConfig = (
  flowId: string | null,
  inputData?: Record<string, unknown>,
) => {
  const [state, setState] = useState<State>(initialState);
  // Serialise inputData so it can be used as a stable useCallback dependency
  // without triggering re-fetches when the caller passes a new object literal.
  const inputDataJson = JSON.stringify(inputData);

  const fetchConfig = useCallback(async () => {
    if (!flowId) {
      setState({
        loading: false,
        configuration: null,
        error: 'No flow ID provided',
      });
      return;
    }

    setState({ loading: true, configuration: null });

    try {
      const resp = await fetch('/catalog/api/enrichard-form-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flowId,
          inputData: inputDataJson ? JSON.parse(inputDataJson) : undefined,
        }),
      });

      if (!resp.ok) {
        // Our proxy (enrichard-form-config.ts) forwards enrichard's own
        // { error: string } body on failure — surface that message rather
        // than a generic one, so e.g. invalid-input errors are actionable.
        let message = `Request failed with status ${resp.status}`;
        try {
          const errorBody = await resp.json();
          if (typeof errorBody?.error === 'string' && errorBody.error) {
            message = errorBody.error;
          }
        } catch {
          // Body wasn't JSON (or empty) — fall back to the generic message.
        }
        throw new FormConfigRequestError(message, resp.status);
      }

      const data = await resp.json();
      setState({ loading: false, configuration: data.configuration ?? null });
    } catch (err) {
      setState({
        loading: false,
        configuration: null,
        error: err instanceof Error ? err.message : 'Unknown error',
        errorStatus:
          err instanceof FormConfigRequestError ? err.status : undefined,
      });
    }
  }, [flowId, inputDataJson]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return { ...state, refetch: fetchConfig };
};
